import { Box } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

import { TaxSeasonBanner, type TaxSeasonBannerSourceUI } from "./TaxSeasonBanner";

const TAX_SEASON_BANNER_DISMISSED_KEY_PREFIX = "tax-season-banner-dismissed";

interface TaxSeasonBannerContainerProps {
  sourceUI: TaxSeasonBannerSourceUI;
}

export function TaxSeasonBannerContainer(props: TaxSeasonBannerContainerProps) {
  const { sourceUI } = props;

  const worker = useDefinedWorker();
  const workerTimezone = worker.tmz;

  const form1099UiConfig = useCbhFlag(CbhFeatureFlag.FORM_1099_UI, {
    defaultValue: { enabled: false },
  });

  const taxationYear = form1099UiConfig.enabled ? form1099UiConfig.taxationYear : 0;

  const [isDismissed, setIsDismissed] = useLocalStorage(
    `${TAX_SEASON_BANNER_DISMISSED_KEY_PREFIX}-${taxationYear}`,
    false
  );

  const isBannerVisible = form1099UiConfig.enabled && !isDismissed;

  useLogEffect(
    APP_V2_USER_EVENTS.TAX_SEASON_BANNER_SHOWN,
    {
      sourceUI,
      timezone: workerTimezone,
    },
    { enabled: isBannerVisible }
  );

  if (!isBannerVisible) {
    return null;
  }

  const { formIssuanceThresholdInDollars } = form1099UiConfig;

  const handleClose = () => {
    setIsDismissed(true);
  };

  return (
    // Box is added here as it's a "div" required for TaxSeasonBanner to
    // have a way to grow in height more than the default height of the banner.
    // This is specifically required in the Shift Discovery page as the banner
    // is placed in a container that is taller than the banner itself due to the clipboard card.
    <Box>
      <TaxSeasonBanner
        sourceUI={sourceUI}
        timezone={workerTimezone}
        taxationYear={taxationYear}
        formIssuanceThresholdInDollars={formIssuanceThresholdInDollars}
        onClose={handleClose}
      />
    </Box>
  );
}
